import { useMemo, useState } from 'react';
import { Button, InlineAlert } from '@/contasis';
import { MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION, OPCIONES_ESTADO_ANIMO } from '../constantes';
import { useToast } from '@/contexts/ContextoToast';
import { CampoComentarioBreve } from './CampoComentarioBreve';
import { useEstadoEnvioLocal } from '../hooks/useEstadoEnvioLocal';
import { useRetroalimentacion } from '../hooks/useRetroalimentacion';
import { normalizarTextoBreve } from '../reglasFrecuencia';
import type { EstadoAnimoId } from '../tipos';

export function EstadoAnimoDiario() {
  const { enviarEstadoAnimo, cerrarPanel } = useRetroalimentacion();
  const { showError, showWarning } = useToast();
  const [estadoSeleccionado, setEstadoSeleccionado] = useState<EstadoAnimoId | null>(null);
  const [comentario, setComentario] = useState('');
  const { enviado, enviando, ejecutarEnvioLocal, reiniciarEstadoEnvio } = useEstadoEnvioLocal();

  const opcionSeleccionada = useMemo(
    () => OPCIONES_ESTADO_ANIMO.find((opcion) => opcion.id === estadoSeleccionado) ?? null,
    [estadoSeleccionado],
  );

  const comentarioNormalizado = normalizarTextoBreve(comentario);
  const puedeEnviar = estadoSeleccionado !== null && !enviando;

  const seleccionarEstado = (estado: EstadoAnimoId) => {
    setEstadoSeleccionado(estado);

    if (enviado) {
      reiniciarEstadoEnvio();
    }
  };

  const actualizarComentario = (valor: string) => {
    setComentario(valor);

    if (enviado) {
      reiniciarEstadoEnvio();
    }
  };

  const enviar = async () => {
    if (!estadoSeleccionado) {
      showWarning('Selecciona una opción antes de continuar.');
      return;
    }

    const ok = await ejecutarEnvioLocal(() => enviarEstadoAnimo({
      estado: estadoSeleccionado,
      comentario: comentarioNormalizado || undefined,
    }));

    if (ok === 'omitido') {
      return;
    }

    if (ok === 'fallido') {
      showError('No pudimos guardar tu respuesta. Inténtalo de nuevo.');
      return;
    }

    setEstadoSeleccionado(null);
    setComentario('');
  };

  if (enviado) {
    return (
      <div className="space-y-5">
        <InlineAlert variant="success" title="Gracias por compartir tu estado de ánimo" />

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={reiniciarEstadoEnvio}>
            Registrar otro estado
          </Button>
          <Button variant="primary" onClick={cerrarPanel}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h3 className="text-ui-base font-semibold text-primary">¿Cómo va tu día?</h3>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {OPCIONES_ESTADO_ANIMO.map((opcion) => {
          const activa = opcion.id === estadoSeleccionado;

          return (
            <button
              key={opcion.id}
              type="button"
              onClick={() => seleccionarEstado(opcion.id)}
              disabled={enviando}
              className={`rounded-lg border p-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-40 disabled:cursor-not-allowed ${activa
                ? 'border-brand bg-surface-selected ring-1 ring-brand'
                : 'border-default bg-surface-0 hover:bg-surface-hover'
                }`}
              aria-pressed={activa}
            >
              <div className="text-2xl">{opcion.emoji}</div>
              <div className="mt-2 text-ui-sm font-semibold text-primary">{opcion.etiqueta}</div>
              <div className={`mt-1 text-caption ${activa ? 'text-secondary' : 'text-tertiary'}`}>
                {opcion.descripcion}
              </div>
            </button>
          );
        })}
      </div>

      <CampoComentarioBreve
        etiqueta="Comentario opcional"
        valor={comentario}
        onChange={actualizarComentario}
        placeholder={opcionSeleccionada ? `Si quieres, cuéntanos por qué elegiste “${opcionSeleccionada.etiqueta}”.` : 'Si quieres, agrega un comentario breve.'}
        maximoCaracteres={MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION}
        disabled={enviando}
      />

      <div className="flex justify-end">
        <Button variant="primary" onClick={enviar} disabled={!puedeEnviar} loading={enviando} loadingText="Enviando...">
          Enviar estado de ánimo
        </Button>
      </div>
    </div>
  );
}
